import React, { useEffect, useState } from 'react';
import { Plus, Edit, Trash2, UserCheck, Phone, KeyRound, ShieldCheck } from 'lucide-react';
import { sellersApi, rolesApi } from '../api';
import { Modal } from '../components/ui/Modal';
import { StatusBadge } from '../components/ui/StatusBadge';
import { EmptyState } from '../components/ui/EmptyState';
import { SkeletonCards } from '../components/ui/Skeleton';
import { useToast } from '../components/ui/ToastProvider';
import { useConfirm } from '../components/ui/ConfirmProvider';
import type { Seller, Role } from '../types';

interface SellerFormData {
  name: string;
  phone: string;
  telegram_id: number | '';
  is_active: boolean;
  role: number | '';
  monthly_salary: string;
  payday_day: number | '';
  username: string;
  password: string;
}

const initialFormData: SellerFormData = {
  name: '',
  phone: '',
  telegram_id: '',
  is_active: true,
  role: '',
  monthly_salary: '',
  payday_day: 1,
  username: '',
  password: '',
};

export const Sellers: React.FC = () => {
  const toast = useToast();
  const confirm = useConfirm();
  const [sellers, setSellers] = useState<Seller[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<SellerFormData>(initialFormData);
  const [hasWebLogin, setHasWebLogin] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<number | null>(null);

  const fetchSellers = async () => {
    setLoading(true);
    try {
      const res = await sellersApi.list();
      const data = Array.isArray(res.data) ? res.data : (res.data as any).results || [];
      setSellers(data);
    } catch (error) {
      console.error('Sellers fetch error:', error);
      toast.error('Sotuvchilarni yuklashda xatolik yuz berdi');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const res = await rolesApi.list();
      const data = Array.isArray(res.data) ? res.data : (res.data as any).results || [];
      setRoles(data);
    } catch (error) {
      console.error('Roles fetch error:', error);
    }
  };

  useEffect(() => {
    fetchSellers();
    fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openAddModal = () => {
    setEditingId(null);
    setFormData(initialFormData);
    setHasWebLogin(false);
    setShowModal(true);
  };

  const openEditModal = async (id: number) => {
    try {
      const res = await sellersApi.get(id);
      const s = res.data;
      setFormData({
        name: s.name,
        phone: s.phone,
        telegram_id: s.telegram_id || '',
        is_active: s.is_active,
        role: s.role || '',
        monthly_salary: s.monthly_salary ? String(Math.round(Number(s.monthly_salary))) : '',
        payday_day: s.payday_day || 1,
        username: s.web_username || '',
        password: '',
      });
      setHasWebLogin(s.has_web_login);
      setEditingId(id);
      setShowModal(true);
    } catch (error) {
      console.error('Error loading seller:', error);
      toast.error('Sotuvchini yuklashda xato');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setFormData(initialFormData);
    setHasWebLogin(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload: Record<string, any> = {
        name: formData.name,
        phone: formData.phone,
        telegram_id: formData.telegram_id || null,
        is_active: formData.is_active,
        role: formData.role || null,
        monthly_salary: formData.monthly_salary || '0',
        payday_day: formData.payday_day || 1,
      };
      // Web login — faqat username kiritilgan bo'lsa yuboramiz
      if (formData.username.trim()) {
        payload.username = formData.username.trim();
      }
      if (formData.password.trim()) {
        payload.password = formData.password.trim();
      }

      if (editingId) {
        await sellersApi.update(editingId, payload);
        toast.success('Sotuvchi yangilandi');
      } else {
        await sellersApi.create(payload);
        toast.success('Sotuvchi qo\'shildi');
      }
      closeModal();
      fetchSellers();
    } catch (error: any) {
      console.error('Save error:', error);
      const data = error.response?.data;
      const msg = data?.username || data?.detail || data?.error || 'Saqlashda xato yuz berdi';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number, name: string) => {
    const confirmed = await confirm({
      title: "Sotuvchini o'chirish",
      description: `"${name}" sotuvchisini o'chirmoqchimisiz? Uning savdo tarixi (buyurtmalar va chiqim yozuvlari) ismi bilan saqlanib qoladi, lekin xodim ro'yxatdan chiqadi va tizimga kira olmaydi. Bu amalni ortga qaytarib bo'lmaydi.`,
      confirmLabel: "O'chirish",
      danger: true,
    });
    if (!confirmed) return;

    setDeleting(id);
    try {
      await sellersApi.delete(id);
      toast.success("Sotuvchi o'chirildi");
      fetchSellers();
    } catch (error: any) {
      console.error('Delete error:', error);
      // Server nima uchun ruxsat bermaganini aytadi (masalan savdo tarixi bor)
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : "O'chirishda xato yuz berdi");
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="page-bar flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Sotuvchilar</h1>
        <button onClick={openAddModal} className="btn btn-primary flex items-center gap-2">
          <Plus size={20} />
          <span>Yangi sotuvchi</span>
        </button>
      </div>

      {loading ? (
        <SkeletonCards />
      ) : sellers.length === 0 ? (
        <div className="card">
          <EmptyState icon={UserCheck} title="Sotuvchi topilmadi" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sellers.map((seller) => (
            <div key={seller.id} className="card">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-bold text-lg text-gray-900">{seller.name}</h3>
                  <div className="flex items-center gap-1 text-gray-500 text-sm mt-1">
                    <Phone size={14} />
                    {seller.phone}
                  </div>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => openEditModal(seller.id)}
                    className="p-2 text-primary-800 hover:bg-primary-50 rounded-lg"
                  >
                    <Edit size={18} />
                  </button>
                  <button
                    onClick={() => handleDelete(seller.id, seller.name)}
                    disabled={deleting === seller.id}
                    className="p-2 text-danger-600 hover:bg-danger-50 rounded-lg disabled:opacity-50"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>

              <div className="mt-3 pt-3 border-t border-gray-100 space-y-2 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Rol:</span>
                  {seller.role_name ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-50 text-primary-800 text-xs font-medium">
                      <ShieldCheck size={12} />
                      {seller.role_name}
                    </span>
                  ) : (
                    <span className="text-gray-400">Yo'q</span>
                  )}
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Oylik:</span>
                  <span className="font-medium">
                    {Number(seller.monthly_salary) > 0
                      ? `${Number(seller.monthly_salary).toLocaleString('uz-UZ')} so'm · ${seller.payday_day}-sana`
                      : <span className="text-gray-400">Belgilanmagan</span>}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Telegram ID:</span>
                  <span className="font-mono">{seller.telegram_id || '-'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Web login:</span>
                  {seller.has_web_login ? (
                    <span className="inline-flex items-center gap-1 text-success-600 font-medium">
                      <KeyRound size={13} />
                      {seller.web_username}
                    </span>
                  ) : (
                    <span className="text-gray-400">Yo'q</span>
                  )}
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Holati:</span>
                  <StatusBadge tone={seller.is_active ? 'success' : 'neutral'}>
                    {seller.is_active ? 'Faol' : 'Nofaol'}
                  </StatusBadge>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal
        open={showModal}
        onClose={closeModal}
        title={editingId ? 'Sotuvchini tahrirlash' : 'Yangi sotuvchi'}
        size="sm"
        footer={
          <>
            <button type="button" onClick={closeModal} className="btn btn-secondary">
              Bekor qilish
            </button>
            <button type="submit" form="seller-form" disabled={saving} className="btn btn-primary">
              {saving ? 'Saqlanmoqda...' : 'Saqlash'}
            </button>
          </>
        }
      >
        <form id="seller-form" onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label">Ism *</label>
            <input
              type="text"
              className="input"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div>
            <label className="label">Telefon *</label>
            <input
              type="tel"
              className="input"
              placeholder="+998901234567"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              required
            />
          </div>

          {/* Rol */}
          <div>
            <label className="label">Rol</label>
            <select
              className="input"
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value ? Number(e.target.value) : '' })}
            >
              <option value="">Rolsiz</option>
              {roles.map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Ruxsatlar rol orqali beriladi (chegirma, narx tahrirlash, asl narxni ko'rish)
            </p>
          </div>

          {/* Oylik — avans hisobi shu ikki maydonga tayanadi */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Oylik (so'm)</label>
              <input
                type="number"
                min="0"
                step="any"
                className="input"
                placeholder="0"
                value={formData.monthly_salary}
                onChange={(e) => setFormData({ ...formData, monthly_salary: e.target.value })}
              />
            </div>
            <div>
              <label className="label">Maosh kuni</label>
              <input
                type="number"
                min="1"
                max="31"
                className="input"
                value={formData.payday_day}
                onChange={(e) => setFormData({ ...formData, payday_day: e.target.value ? Number(e.target.value) : '' })}
              />
            </div>
          </div>
          <p className="text-xs text-gray-500 -mt-2">
            Avans shu oylikdan ayriladi. Maosh kuni har sotuvchida har xil bo'lishi mumkin (1–31).
          </p>

          <div>
            <label className="label">Telegram ID</label>
            <input
              type="number"
              className="input"
              placeholder="123456789"
              value={formData.telegram_id}
              onChange={(e) => setFormData({ ...formData, telegram_id: e.target.value ? Number(e.target.value) : '' })}
            />
            <p className="text-xs text-gray-500 mt-1">Mini App orqali sotish uchun</p>
          </div>

          {/* Web login */}
          <div className="border-t border-gray-100 pt-4">
            <div className="flex items-center gap-2 mb-2">
              <KeyRound size={16} className="text-gray-500" />
              <span className="text-sm font-medium">Web adminka logini</span>
            </div>
            <div className="space-y-2">
              <input
                type="text"
                className="input"
                placeholder="Login (username)"
                autoComplete="off"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              />
              <input
                type="password"
                className="input"
                placeholder={hasWebLogin ? "Yangi parol (o'zgartirmasa bo'sh qoldiring)" : 'Parol'}
                autoComplete="new-password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {hasWebLogin
                ? "Bu sotuvchi web adminkaga kira oladi. Parolni o'zgartirish uchun yangi parol yozing."
                : "Bo'sh qoldirsangiz — faqat Telegram orqali ishlaydi, web'ga kira olmaydi."}
            </p>
          </div>

          <label className="flex items-center gap-2 cursor-pointer border-t border-gray-100 pt-4">
            <input
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm">Faol</span>
          </label>
        </form>
      </Modal>
    </div>
  );
};
